import React, { useEffect, useState } from 'react';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { Button } from '@swagup-com/components';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import isEqual from 'lodash/isEqual';

import ItemsDecorationPanel from './ItemsDecorationPanel';
import { formatColor } from './common';
import { productStatus } from '../../../apis/constants';
import Drawer from '../../pages/orders/requested/common/Drawer';
import ColorSection from './ColorSection';
import ItemsChangesHistory from './ItemsChangesHistory';
import { changeColorLightness } from '../../shared/styles/utils';

const useStyles = makeStyles({
  paper: {
    maxWidth: 448,
    padding: '36px 40px 0px 32px'
  },
  historyBtn: {
    fontFamily: 'Gilroy',
    fontSize: 14,
    fontWeight: 500,
    color: '#3577d4',
    height: 'fit-content',
    minWidth: 'auto',
    padding: 0,
    '&:hover': { color: changeColorLightness('#3577d4') }
  }
});

// Given two objects (a proof item) an a copy, it returns a object with the properties that are different
// it also preform some other taks like adding the API name (which not changes) for the case of decorations
const getDifferencesBetweenTwoObjects = (obj1, obj2 = {}, property) => {
  let diff = {};

  if (isArray(obj1)) {
    for (let i = 0; i < obj1.length; i++) {
      const deepUpdates = getDifferencesBetweenTwoObjects(obj1[i], obj2 ? obj2[i] : {}, property);
      diff = isEmpty(deepUpdates)
        ? diff
        : {
            ...diff,
            [`${property}_${i + 1}`]: { ...deepUpdates, Name: obj2[i]?.Name || obj1[i]?.Name } // propertyName_# = (differences, Name from API)
          };
    }
    return diff;
  }

  if (isPlainObject(obj1)) {
    diff = Object.keys(obj1).reduce((result, key) => {
      if (!obj2.hasOwnProperty(key)) {
        result.push(key);
      } else if (isEqual(obj1[key], obj2[key])) {
        const resultKeyIndex = result.indexOf(key);
        result.splice(resultKeyIndex, 1);
      }
      return result;
    }, Object.keys(obj2));
    let rslt = {};
    diff
      .filter(k => k !== 'created' && k !== 'updated' && k !== 'deleted') // these properties are left out the comparison
      .forEach(key => {
        const deepUpdates = getDifferencesBetweenTwoObjects(obj1[key], obj2[key], key);
        rslt =
          isEmpty(deepUpdates) && isPlainObject(obj1[key])
            ? rslt
            : {
                ...rslt,
                [key]: deepUpdates
              };
      });
    return isEmpty(rslt) && !obj1.deleted
      ? rslt
      : {
          ...rslt,
          ...diff
            .filter(k => k === 'created' || k === 'updated' || k === 'deleted') // these properties values are back included ...
            .reduce((s, p) => ({ ...s, [p]: obj1[p] }), {}) // ....................when a differences are found
        };
  }
  return isEqual(obj1, obj2) ? undefined : obj1;
};

const markItemsDecorations = item => ({
  ...item,
  decorations: item.decorations
    ? item.decorations.map((d, idx) => ({
        ...d,
        id: idx,
        expanded: idx === item.decorations.length - 1 && item.status !== productStatus.designReady
      }))
    : []
});

const parseChanges = (changes, item) => {
  const apiModel = {
    ...item,
    itemColor: { theme_color_hex: formatColor(item.theme_color_hex), theme_color_name: item.theme_color }
  };
  return { target: changes, source: apiModel };
};

const generateItemChangesRequest = (changes, item) => {
  const { target, source } = parseChanges(changes, item);
  return getDifferencesBetweenTwoObjects(target, source);
};

const getColorHexValue = value => (value ? `#${value?.replace('#', '')}` : undefined);

const FeedbackSection = ({
  item,
  onChangesRequested,
  changesHistory,
  canBeApproved,
  canApproveProd,
  statusLoading,
  busy,
  onSubmitChanges,
  setOpenConfirmationDialog,
  approveBtnText
}) => {
  const classes = useStyles();

  const [changes, setChanges] = useState({});
  const [openHistory, setOpenHistory] = useState(false);

  const onToggleHistory = () => setOpenHistory(prev => !prev);

  useEffect(() => {
    setChanges(markItemsDecorations(item));
  }, [item]);

  useEffect(() => {
    onChangesRequested(generateItemChangesRequest(changes, markItemsDecorations(item)));
  }, [changes, item, onChangesRequested]);

  const localDecorations = changes.decorations?.filter(d => !d.deleted);
  const disableDecorationsChanges = item.status !== productStatus.designReady;
  const disableDecorations = item.status === productStatus.changesRequested;
  const isColorSectionDisabled =
    !item.available_colors || item.available_colors.length === 0 || disableDecorations || disableDecorationsChanges;
  const showDecorations = item.status !== productStatus.pendingMockup && item.status !== productStatus.inProgress;
  const showDecorationAddForm = item.status === productStatus.designReady;

  const handleDelete = id =>
    setChanges(prevChanges => ({
      ...prevChanges,
      decorations: prevChanges.decorations.map(d => (d.id === id ? { ...d, deleted: true } : d))
    }));

  const handleEditDecoration = change =>
    setChanges(prev => ({
      ...prev,
      decorations: prev.decorations.map(d => (d.id === change.id ? { ...change, updated: true } : d))
    }));

  const handleAddDecoration = change =>
    setChanges(prev => ({
      ...prev,
      decorations:
        !isEmpty(prev.decorations) && prev.decorations.find(d => d.id === change.id)
          ? prev.decorations.map(d => (d.id === change.id ? change : d))
          : [...(prev.decorations || []), change]
    }));

  const itemColor = changes?.itemColor;
  const [themeColorHex, themeColorName] = itemColor
    ? [getColorHexValue(itemColor.theme_color_hex), itemColor.theme_color_name]
    : [getColorHexValue(changes.theme_color_hex), changes.theme_color];

  const handleColorSelection = color =>
    setChanges({
      ...changes,
      itemColor: {
        theme_color_name: color.name,
        theme_color_hex: formatColor(getColorHexValue(color.hex))
      }
    });

  return (
    <Box height="100%">
      <Grid container>
        <Grid container className={classes.ctaWrapper}>
          <Grid item xs>
            <ColorSection
              key={item.id}
              itemStatus={item.status}
              colors={changes.available_colors}
              selectedColor={{ name: themeColorName, hex: themeColorHex }}
              onClick={handleColorSelection}
              disabled={isColorSectionDisabled}
            />
          </Grid>
        </Grid>

        <Grid container className={classes.ctaWrapper}>
          {showDecorations && (
            <Grid item xs={12}>
              {localDecorations?.map(decoration => (
                <ItemsDecorationPanel
                  key={decoration.id}
                  item={changes}
                  decoration={decoration}
                  onChange={handleEditDecoration}
                  onDelete={handleDelete}
                  disabled={disableDecorationsChanges}
                  changes={changes}
                />
              ))}
            </Grid>
          )}
          {showDecorationAddForm && (
            <Grid item xs={12}>
              <ItemsDecorationPanel
                item={changes}
                decoration={{
                  id: isEmpty(changes.decorations) ? 1 : Math.max(...changes.decorations.map(d => d.id)) + 1,
                  created: true,
                  expanded: true
                }}
                onChange={handleAddDecoration}
                onDelete={handleDelete}
                disabled={disableDecorationsChanges}
                changes={changes}
                isNewForm
              />
            </Grid>
          )}
        </Grid>

        <Grid container justifyContent="space-between" alignItems="center" style={{ marginTop: 30 }}>
          <Grid item>
            {canBeApproved ? (
              <Button
                variant="primary"
                size="small"
                disabled={!canApproveProd || statusLoading}
                onClick={() => setOpenConfirmationDialog(true)}
                style={{ height: 40 }}
                loading={statusLoading}
              >
                {approveBtnText}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="small"
                disabled={busy}
                onClick={onSubmitChanges}
                loading={busy}
                style={{ height: 40 }}
              >
                Submit Changes
              </Button>
            )}
          </Grid>
          <Grid item>
            <Button
              variant="text"
              disabled={!changesHistory.results?.length}
              onClick={onToggleHistory}
              className={classes.historyBtn}
            >
              Changes History
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Drawer open={openHistory} onClose={onToggleHistory} classes={classes}>
        <ItemsChangesHistory productName={item.name} changesHistory={changesHistory} onContinue={onToggleHistory} />
      </Drawer>
    </Box>
  );
};

export default FeedbackSection;
